import React, { useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import Card from '@material-ui/core/Card'
import Typography from '@material-ui/core/Typography'
import Avatar from '@material-ui/core/Avatar'
import Button from '@material-ui/core/Button'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import Link from 'next/link'
import { makeStyles } from '@material-ui/core/styles'
import CoachLogo from '@components/coach-logo'
import defaults from '@config/defaults'
import { composeOptimizedImageUrl } from '@utils/composeOptimizedImageUrl'
import * as amplitude from '@amplitude/analytics-browser'
import { PGA_COACH_JOURNEY_ANALYTICS } from '@lib/pga-coach/constants'
import { MapPin, COACH_PROFILE_META_ICON_PROPS } from '@lib/pga-coach/coach-profile-icons'
import useOnScreen from '@hooks/useOnScreen'
import { getCoachHitFacility } from '@lib/algolia/coach-geoloc'

const useStyles = makeStyles((theme) => ({
  card: {
    borderRadius: 12,
    border: '1px solid #dfe3ea',
    boxShadow: 'none',
    padding: theme.spacing(1.5, 1.5),
    cursor: 'pointer',
    transition: 'border-color 150ms ease, box-shadow 150ms ease',
    '&:hover': {
      borderColor: '#9aa9bd',
      boxShadow: '0 2px 10px rgba(15, 34, 52, 0.06)',
    },
  },
  link: {
    textDecoration: 'none',
    color: 'inherit',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flex: 1,
    minWidth: 0,
  },
  inner: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  avatar: {
    width: 48,
    height: 48,
    flexShrink: 0,
    [theme.breakpoints.down('xs')]: {
      width: 40,
      height: 40,
    },
  },
  coachInfo: {
    flex: 1,
    minWidth: 0,
  },
  nameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    flexWrap: 'wrap',
  },
  name: {
    fontWeight: 600,
    fontSize: '1rem',
    lineHeight: 1.2,
    color: theme.palette.blue.main,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  subtitle: {
    color: '#6d7784',
    fontSize: '0.875rem',
    lineHeight: 1.3,
    marginTop: 2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '80%',
  },
  facilityRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginTop: 2,
    color: '#4f5966',
  },
  facilityIcon: {
    flexShrink: 0,
    color: theme.palette.secondary.main,
  },
  facilityName: {
    fontSize: '0.875rem',
    lineHeight: 1.3,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    justifyContent: 'flex-start',
    flexShrink: 0,
  },
  registerButton: {
    '&.MuiButton-root': {
      borderRadius: 999,
    },
    minHeight: 32,
    fontSize: theme.typography.pxToRem(14),
    lineHeight: 1.2,
    padding: theme.spacing(0.75, 2.25),
    whiteSpace: 'nowrap',
    textTransform: 'none',
    fontWeight: 600,
    boxShadow: 'none',
  },
}))

function ExternallyBookableCoachCard({
  coach,
  trackingSource = 'results',
  position,
}) {
  const classes = useStyles()
  const impressionRef = useRef(null)
  const lastViewedKeyRef = useRef(null)
  const isCardVisible = useOnScreen(impressionRef)

  const {
    objectID,
    displayName,
    firstName,
    lastName,
    profilePhoto,
    title,
    facilities,
    bookingURL,
    memberType,
    certificationLevel,
    customSlug,
    hasPlusSubscription,
    _rankingInfo,
  } = coach

  const name = (firstName && lastName) ? `${firstName} ${lastName}` : (displayName || 'PGA Coach')
  const facilityName = getCoachHitFacility({ facilities, _rankingInfo })?.facilityName || null
  const photoUrl = profilePhoto
    ? composeOptimizedImageUrl(profilePhoto, 96)
    : defaults.coachPlaceholder
  const analyticsPayload = useMemo(() => ({
    source: trackingSource,
    coach_id: objectID,
    coach_name: name,
    facility: facilityName,
    has_plus_subscription: hasPlusSubscription,
    member_type: memberType,
    certification_level: certificationLevel,
    position,
  }), [certificationLevel, facilityName, hasPlusSubscription, memberType, name, objectID, position, trackingSource])

  useEffect(() => {
    const viewKey = `${trackingSource}:${objectID}`
    if (!isCardVisible || lastViewedKeyRef.current === viewKey) return
    lastViewedKeyRef.current = viewKey
    amplitude.track(PGA_COACH_JOURNEY_ANALYTICS.externallyBookableCoachCardImpression, analyticsPayload)
  }, [analyticsPayload, isCardVisible, objectID, trackingSource])

  const trackCardClick = (clickTarget) => {
    amplitude.track(PGA_COACH_JOURNEY_ANALYTICS.externallyBookableCoachCardClick, {
      ...analyticsPayload,
      click_target: clickTarget,
    })
  }

  return (
    <Card ref={impressionRef} className={classes.card}>
      <div className={classes.inner}>
        <Link
          href={`/coach/${customSlug}`}
          className={classes.link}
          onClick={() => trackCardClick('profile')}
        >
          <Avatar
            className={classes.avatar}
            src={photoUrl}
            alt={name}
          />

          <div className={classes.coachInfo}>
            <div className={classes.nameRow}>
              <Typography className={classes.name} variant="body2">
                {name}
              </Typography>
              <CoachLogo memberType={memberType} certificationLevel={certificationLevel} size="16px" />
            </div>
            {title && (
              <Typography className={classes.subtitle}>
                {title}
              </Typography>
            )}
            {facilityName && (
              <div className={classes.facilityRow}>
                <MapPin {...COACH_PROFILE_META_ICON_PROPS} className={classes.facilityIcon} />
                <Typography className={classes.facilityName} variant="body2" color="secondary">
                  {facilityName}
                </Typography>
              </div>
            )}
          </div>
        </Link>

        <div className={classes.actions}>
          {bookingURL && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              className={classes.registerButton}
              href={bookingURL}
              target="_blank"
              rel="noopener noreferrer"
              endIcon={<OpenInNewIcon fontSize="small" />}
              onClick={(event) => {
                event.stopPropagation()
                trackCardClick('register')
              }}
              component="a"
            >
              Register
            </Button>
          )}
        </div>
      </div>
    </Card>
  )
}

ExternallyBookableCoachCard.propTypes = {
  coach: PropTypes.shape({
    objectID: PropTypes.string.isRequired,
    displayName: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    profilePhoto: PropTypes.string,
    title: PropTypes.string,
    facilities: PropTypes.arrayOf(PropTypes.shape({
      facilityName: PropTypes.string,
      lat: PropTypes.number,
      lng: PropTypes.number,
    })),
    bookingURL: PropTypes.string,
    memberType: PropTypes.string,
    certificationLevel: PropTypes.string,
    customSlug: PropTypes.string,
    hasPlusSubscription: PropTypes.bool,
    _rankingInfo: PropTypes.shape({
      matchedGeoLocation: PropTypes.shape({
        lat: PropTypes.number,
        lng: PropTypes.number,
        distance: PropTypes.number,
      }),
    }),
  }).isRequired,
  trackingSource: PropTypes.string,
  position: PropTypes.number,
}

export default ExternallyBookableCoachCard
